import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/site/PageHeader";
import { RichText } from "@/components/site/RichText";
import { readBodyFormat } from "@/lib/rich-body";
import { readBuilderBlocks } from "@/components/site/EditablePageShell";
import { BuilderBlocks } from "@/components/admin/page-builder/renderBuilderBlocks";
import { customPageQueryOptions } from "@/lib/pages.functions";

const SITE_URL = "https://glowlonggolf.com";

/**
 * Catch-all for pages an admin created in the admin Pages tab. Coded routes
 * (static files) always win over this route, so it only serves brand-new
 * database pages — which means new pages go live on Publish, no deploy.
 */
export const Route = createFileRoute("/$slug")({
  loader: async ({ context, params }) => {
    const page = await context.queryClient.ensureQueryData(customPageQueryOptions(params.slug));
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData, params }) => ({
    meta: [
      { title: loaderData?.title || "Page unavailable — Glow Long Golf" },
      {
        name: "description",
        content: loaderData?.meta_description || "Glow-in-the-dark golf balls for night golf, gifts, and events.",
      },
      { property: "og:title", content: loaderData?.title || "Glow Long Golf" },
      {
        property: "og:description",
        content: loaderData?.meta_description || "Glow-in-the-dark golf balls for night golf, gifts, and events.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      ...(loaderData ? [] : [{ name: "robots", content: "noindex" }]),
    ],
    links: loaderData ? [{ rel: "canonical", href: `${SITE_URL}/${params.slug}` }] : [],
  }),
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive" role="alert">
      {error.message}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Page not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        This page doesn&apos;t exist or hasn&apos;t been published yet.
      </p>
    </div>
  ),
  component: CustomPage,
});

function CustomPage() {
  const { slug } = Route.useParams();
  const { data: page } = useSuspenseQuery(customPageQueryOptions(slug));
  if (!page) return null;

  const blocks = readBuilderBlocks((page.extra as any)?.builder);

  return (
    <>
      <PageHeader
        eyebrow={page.hero_eyebrow || undefined}
        title={page.hero_title || page.title}
        lead={page.hero_lead || undefined}
      />
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        {page.hero_image_url && (
          <div className="mb-8 overflow-hidden rounded-3xl border border-border bg-card">
            <img src={page.hero_image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
          </div>
        )}
        {blocks.length > 0 ? (
          <BuilderBlocks blocks={blocks} />
        ) : (
          <RichText markdown={page.body_md} format={readBodyFormat(page.extra)} />
        )}
      </section>
    </>
  );
}
